import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useTheme } from '@mui/material/styles';

import { ProcessedParcel, FilterMode } from './types';
import { computeProgress } from './utils';
import { useCustomer } from '../../customer';

export type SummaryStatsProps = {
  rows: ProcessedParcel[];
  filter: FilterMode;
  onFilterChange: (filter: FilterMode) => void;
};

type TileTone = 'success' | 'warning' | 'danger';

type TileProps = {
  label: string;
  value: React.ReactNode;
  delta?: string;
  icon: React.ReactNode;
  tone: TileTone;
  active?: boolean;
  onClick?: () => void;
};

const Tile = ({ label, value, delta, icon, tone, active, onClick }: TileProps) => {
  const theme = useTheme();
  const toneMap: Record<TileTone, { bg: string; fg: string; ring: string }> = {
    success: { bg: theme.palette.success.light, fg: theme.palette.success.main, ring: theme.palette.success.main },
    warning: { bg: theme.palette.warning.light, fg: theme.palette.warning.main, ring: theme.palette.warning.main },
    danger:  { bg: theme.palette.error.light,   fg: theme.palette.error.main,   ring: theme.palette.error.main   },
  };
  const t = toneMap[tone];
  const interactive = Boolean(onClick);

  return (
    <ButtonBase
      onClick={onClick}
      disabled={!interactive}
      focusRipple
      aria-pressed={active ? 'true' : undefined}
      sx={{
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: active ? t.ring : 'divider',
        boxShadow: active ? `0 0 0 3px ${t.bg}` : 'none',
        borderRadius: 2,
        px: 1.75,
        py: 1.25,
        position: 'relative',
        overflow: 'hidden',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease',
        '&:hover': interactive ? {
          borderColor: t.ring,
          backgroundColor: 'grey.50',
        } : undefined,
        '&.Mui-disabled': {
          cursor: 'default',
          opacity: 1,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 1,
          backgroundColor: t.bg,
          color: t.fg,
          display: 'grid',
          placeItems: 'center',
          '& svg': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '-0.3px',
              lineHeight: 1,
              color: 'text.primary',
            }}
          >
            {value}
          </Typography>
          <Typography
            component="span"
            sx={{
              color: 'text.secondary',
              fontSize: 11.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
            }}
          >
            {label}
          </Typography>
        </Box>
        {delta && (
          <Typography
            sx={{
              fontSize: 11.5,
              color: 'text.secondary',
              mt: 0.25,
              lineHeight: 1.3,
            }}
          >
            {delta}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
};

export const SummaryStats = ({ rows, filter, onFilterChange }: SummaryStatsProps) => {
  const { customer } = useCustomer();
  const showRemits = customer.stages.includes('remits');
  let complete = 0;
  let actionNeeded = 0;
  let pendingRemits = 0;

  rows.forEach((row) => {
    const p = computeProgress(row, customer.stages);
    if (p.isComplete) complete += 1;
    else actionNeeded += 1;
    pendingRemits += Math.max(0, p.remitsTotal - p.remitsUploaded);
  });

  // Clicking the currently active tile clears the filter back to 'all'.
  const toggleFilter = (target: FilterMode) =>
    onFilterChange(filter === target ? 'all' : target);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: showRemits ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)' },
        gap: 1.75,
        mb: 2.5,
      }}
    >
      <Tile
        tone="success"
        label="Complete"
        value={complete}
        delta={filter === 'complete' ? 'Click to clear filter' : 'All stages uploaded'}
        icon={<CheckCircleIcon />}
        active={filter === 'complete'}
        onClick={() => toggleFilter('complete')}
      />
      <Tile
        tone="warning"
        label="Action Needed"
        value={actionNeeded}
        delta={filter === 'pending' ? 'Click to clear filter' : 'Awaiting your input'}
        icon={<PendingActionsIcon />}
        active={filter === 'pending'}
        onClick={() => toggleFilter('pending')}
      />
      {showRemits && (
        <Tile
          tone="danger"
          label="Pending Remits"
          value={pendingRemits}
          delta={filter === 'remits' ? 'Click to clear filter' : 'FedEx remittance files'}
          icon={<HourglassTopIcon />}
          active={filter === 'remits'}
          onClick={() => toggleFilter('remits')}
        />
      )}
    </Box>
  );
};
